import React from 'react';
import { useIntl } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { NamedLink } from '../../../components';
import styles from './SectionFeaturedService.module.css';

const getListingImageUrl = listing => {
  const images = listing?.images;
  if (!images || images.length === 0) return null;
  const image = images[0];
  return (
    image?.attributes?.variants?.['listing-card']?.url ||
    image?.attributes?.variants?.['scaled-small']?.url ||
    null
  );
};

const ServiceCard = ({ listing, intl }) => {
  const { id, attributes } = listing;
  const { title, price, publicData } = attributes;
  const imageUrl = getListingImageUrl(listing);

  const priceFormatted = price ? formatMoney(intl, price) : null;
  const priceLabel = priceFormatted ? `From ${priceFormatted}` : null;

  return (
    <NamedLink name="ListingPage" params={{ id: id.uuid, slug: encodeURIComponent(title) }} className={styles.serviceCard}>
      <div className={styles.imageContainer}>
        {imageUrl ? (
          <img src={imageUrl} alt={title} className={styles.serviceImage} />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
      </div>
      <div className={styles.serviceInfo}>
        <h3 className={styles.serviceName}>{title}</h3>
        {priceLabel && <p className={styles.price}>{priceLabel}</p>}
      </div>
    </NamedLink>
  );
};

const SkeletonCard = () => (
  <div className={styles.serviceCard}>
    <div className={`${styles.imageContainer} ${styles.skeleton}`} />
    <div className={styles.serviceInfo}>
      <div className={`${styles.skeletonText} ${styles.skeletonTitle}`} />
      <div className={`${styles.skeletonText} ${styles.skeletonPrice}`} />
    </div>
  </div>
);

/**
 * Featured Services section for the Landing Page.
 * Shows real listings fetched from Sharetribe API (pub_listingType=hourly-booking).
 */
const FeaturedServices = ({ listings = [], inProgress = false }) => {
  const intl = useIntl();
  const showSkeletons = inProgress && listings.length === 0;

  return (
    <section className={styles.featuredSection}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Featured Services Near You</h2>
          <NamedLink
            className={styles.viewAll}
            name="SearchPageWithListingType"
            params={{ listingType: 'services' }}
          >
            View all
          </NamedLink>
        </div>

        <div className={styles.servicesGrid}>
          {showSkeletons
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
            : listings.map(listing => (
                <ServiceCard key={listing.id.uuid} listing={listing} intl={intl} />
              ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedServices;
